import React, { useCallback, useRef, useState } from "react";
import { fireGameStagePopup } from "../../functions/gameStagePopup";
import {
  CHANNEL_OPTIONS,
  STAGE2_SCENARIOS,
  getChannelLabel,
} from "./stage2ChannelData";
import {
  ChannelLightbulbIcon,
  ChannelOptionIcon,
  ChannelScenarioIllus,
} from "./stage2ChannelIcons";

export default function Stage2ChannelChoice({
  onComplete,
  submitting = false,
  themeData,
}) {
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [choices, setChoices] = useState({});
  const [advancing, setAdvancing] = useState(false);
  const [swooshState, setSwooshState] = useState("idle");
  const slideRef = useRef(null);

  const scenario = STAGE2_SCENARIOS[index];
  const total = STAGE2_SCENARIOS.length;
  const isLast = index >= total - 1;

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const waitForAnimation = useCallback((animationName) => {
    const node = slideRef.current;
    if (!node) return Promise.resolve();

    return new Promise((resolve) => {
      const timeout = setTimeout(resolve, 600);
      const onEnd = (event) => {
        if (event.target !== node || event.animationName !== animationName) return;
        clearTimeout(timeout);
        node.removeEventListener("animationend", onEnd);
        resolve();
      };
      node.addEventListener("animationend", onEnd);
    });
  }, []);

  const advanceToNextQuestion = useCallback(async () => {
    if (prefersReducedMotion) {
      setIndex((i) => i + 1);
      setSelected(null);
      return;
    }

    setSwooshState("out");
    await waitForAnimation("stage2-channel-swoosh-out");
    setIndex((i) => i + 1);
    setSelected(null);
    setSwooshState("in");
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    await waitForAnimation("stage2-channel-swoosh-in");
    setSwooshState("idle");
  }, [prefersReducedMotion, waitForAnimation]);

  const handleChoice = useCallback(
    async (channelId) => {
      if (selected || submitting || advancing || !scenario) return;

      const updatedChoices = { ...choices, [scenario.id]: channelId };
      const isCorrect = channelId === scenario.correct;

      setSelected(channelId);
      setChoices(updatedChoices);
      setAdvancing(true);

      const explanationHtml = isCorrect
        ? `<p>${scenario.correctExplanation}</p>`
        : `<p>${scenario.wrongExplanation}</p><p><strong>Best choice: ${getChannelLabel(scenario.correct)}</strong></p>`;

      try {
        await fireGameStagePopup({
          title: isCorrect ? "Correct!" : "Incorrect!",
          html: explanationHtml,
          icon: isCorrect ? "success" : "error",
          variant: isCorrect ? "success" : "error",
          confirmButtonText: "OK",
          themeData,
        });

        if (isLast) {
          await onComplete?.(updatedChoices);
          return;
        }

        await advanceToNextQuestion();
      } finally {
        setAdvancing(false);
      }
    },
    [
      selected,
      submitting,
      advancing,
      scenario,
      choices,
      isLast,
      onComplete,
      themeData,
      advanceToNextQuestion,
    ]
  );

  if (!scenario) return null;

  const swooshClass =
    swooshState === "out"
      ? " stage2-channel__swoosh--out"
      : swooshState === "in"
        ? " stage2-channel__swoosh--in"
        : "";

  return (
    <div className="stage2-channel">
      <div className="stage2-channel__swoosh-wrap">
        <div ref={slideRef} className={`stage2-channel__swoosh${swooshClass}`}>
          <div className="stage2-channel__card">
            <div className="stage2-channel__ribbon">
              Situation {index + 1} of {total}
            </div>

            <div className="stage2-channel__content">
              <div className="stage2-channel__illus" aria-hidden>
                <ChannelScenarioIllus />
              </div>
              <div className="stage2-channel__text">
                <p className="stage2-channel__scenario">{scenario.text}</p>
                <p className="stage2-channel__hint">
                  <span className="stage2-channel__hint-icon" aria-hidden>
                    <ChannelLightbulbIcon />
                  </span>
                  <span className="stage2-channel__hint-text">{scenario.hint}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="stage2-channel__prompt" aria-hidden>
            <span className="stage2-channel__prompt-line" />
            <span className="stage2-channel__prompt-text">Select the best option</span>
            <span className="stage2-channel__prompt-line" />
          </div>

          <div
            className="stage2-channel__options"
            role="group"
            aria-label="Communication channel options"
          >
            {CHANNEL_OPTIONS.map((option) => {
              const picked = selected === option.id;
              const isAnswer = scenario.correct === option.id;
              let stateClass = "";
              if (selected) {
                if (picked && isAnswer) stateClass = "stage2-channel__option--correct";
                else if (picked && !isAnswer) stateClass = "stage2-channel__option--wrong";
                else if (!picked && isAnswer) stateClass = "stage2-channel__option--reveal";
              }

              return (
                <button
                  key={option.id}
                  type="button"
                  className={`stage2-channel__option stage2-channel__option--${option.id}${stateClass ? ` ${stateClass}` : ""}`}
                  disabled={!!selected || submitting || advancing}
                  onClick={() => handleChoice(option.id)}
                >
                  <span className="stage2-channel__option-icon" aria-hidden>
                    <ChannelOptionIcon id={option.id} />
                  </span>
                  <span className="stage2-channel__option-body">
                    <span className="stage2-channel__option-label">{option.label}</span>
                    <span className="stage2-channel__option-desc">{option.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
